import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import ProjectService from "../api/project";
import ProfileService from "../api/profile";
import TemplateTable from "../common/Layout/templatePages/templateTable";
import {Box, CircularProgress, Typography, Divider} from "@mui/material";
import Breadcrumbs from "../common/breadcrumbs";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";

const breadcrumbs = [
    {id: 0, name: "Home", href: "./"},
    {id: 1, name: "Machine Status", href: "machineStatus"},
    {id: 2, name: "History"}
];

function MachineStatusHistory() {
    const organziation = useSelector(({organziation}) => organziation);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const projectId = params.get("projectId");
    const groupId = params.get("groupId");
    const {t} = useTranslation();
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                setLoading(true);
                const orgObj = organziation ?? JSON.parse(localStorage.getItem("defaultOrganization"));
                const apiKey = orgObj?.api_key_list?.[0]?.key;

                const taskResp = await ProjectService.GetProjectTask("", projectId, groupId, "", "Progress", "active", apiKey);
                if (!taskResp.success || !taskResp.count) return setLoading(false);

                const taskId = taskResp.project_task[0].task_id;

                const paramResp = await ProjectService.GetTaskParam("", taskId, "", "", "active", apiKey);
                if (!paramResp.success || !paramResp.count) return setLoading(false);

                const paramList = paramResp.task_param;

                let allParamValueRows = [];
                for (const param of paramList) {
                    const valueResp = await ProjectService.GetParamVal("", param.task_param_id, "", "active", apiKey);
                    if (valueResp.success && valueResp.count) {
                        valueResp.param_value.forEach((val) =>
                            allParamValueRows.push({
                                ...val,
                                param_name: param.name
                            })
                        );
                    }
                }

                const userIds = [...new Set(allParamValueRows.map((row) => row.updated_by_person_id).filter(Boolean))];
                let userMap = {};
                await Promise.all(
                    userIds.map(async (id) => {
                        try {
                            const res = await ProfileService.GetUserProfile({userProfileID: id, apiKey});
                            if (res.success && res.count) {
                                userMap[id] = res.person_profile[0]?.firstname + " " + res.person_profile[0]?.lastname;
                            } else {
                                userMap[id] = id;
                            }
                        } catch {
                            userMap[id] = id;
                        }
                    })
                );

                const flattened = allParamValueRows
                    .sort((a, b) => new Date(b.updated_timestamp) - new Date(a.updated_timestamp))
                    .map((row) => ({
                        param: row.param_name,
                        value: row.value,
                        by: userMap[row.updated_by_person_id] || row.updated_by_person_id,
                        at: row.updated_timestamp
                    }));

                setHistory(flattened);
            } catch (err) {
                setHistory([]);
            }
            setLoading(false);
        };

        fetchHistory();
        // eslint-disable-next-line
    }, [organziation]);

    return (
        <Box
            sx={{
                p: "12px",
                borderRadius: "16px",
                border: `1px solid ${theme.palette.custom.borderColor}`,
                backgroundColor: theme.palette.custom.backgroundColor
            }}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: { xs: "block", sm: "center" },
                    gap: "1rem",
                    mb: "1rem"
                }}>
                <Typography sx={{ fontSize: "45px", mb: 0, color: theme.palette.custom.text }} variant="h2" gutterBottom>
                    {t("MachineStatusHistory")}
                </Typography>
            </Box>
            <Divider sx={{ mb: "10px", backgroundColor: theme.palette.custom.borderColor }} />
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", p: 4}}>
                    <CircularProgress />
                </Box>
            ) : (
                <TemplateTable
                    headCells={[
                        {id: "param", label: "Parameter Name"},
                        {id: "value", label: "Value"},
                        {id: "by", label: "Updated By"},
                        {id: "at", label: "Modified Date", attributeType: "dateTime"}
                    ]}
                    itemsList={history}
                    itemId="at"
                    loading={loading}
                    searchAttributes={["param", "by", "value"]}
                    allowExport
                    exportTitle="Machine Status History"
                    orderBy={3}
                />
            )}
        </Box>
    );
}

export default MachineStatusHistory;
